import { ReactElement } from 'react';
import { Stack } from '@chakra-ui/react';

import fetchContentfulData from 'api/contentful-fetch';
import { ExtendedNextPageContext, UnknownObjectAny } from 'global.types';
import { pageQuery } from 'graphql/queries/page';
import { Banner, ComponentGenerator, Hero } from 'components/CMS';
import { graphQLErrorHandler } from 'utils/graphQLHandlers';
import { CMSPageProps } from 'pages/[categoryPageSlug]/[pageSlug]';
import { FindTravelClinic } from 'components/stores';
import { travelClinicQuery } from 'graphql/queries/locations';
import { TravelClinic } from 'types/cms';

export interface TravelClinicLocationsProps extends CMSPageProps {
  travelClinics: Array<TravelClinic>;
}

const TravelClinicLocations = ({
  content,
  travelClinics
}: TravelClinicLocationsProps): ReactElement => (
  <>
    {content?.pageAlertBanner && <Banner isPageAlertBanner {...content?.pageAlertBanner} />}
    <main>
      <Hero data={content} />
      <Stack spacing="10" marginY={{ base: '5', md: '10' }} alignItems="center">
        {content?.bodyCollection?.items?.map(({ __typename, ...rest }, index) => (
          <ComponentGenerator key={`component-${index}`} __typename={__typename} {...rest} />
        ))}
        <FindTravelClinic travelClinics={travelClinics} />
      </Stack>
    </main>
  </>
);

export async function getServerSideProps({
  req,
  query,
  preview = false
}: ExtendedNextPageContext): Promise<{ props: TravelClinicLocationsProps }> {
  let slug = req?.url?.substring(req?.url?.lastIndexOf('/') + 1)?.replace('.json', '');
  if (typeof query?.pageSlug === 'string') {
    slug = query.pageSlug;
  }

  const content = (await fetchContentfulData(
    {
      query: pageQuery,
      variables: {
        slug,
        preview
      }
    },
    preview
  )) as UnknownObjectAny;

  const travelClinics = (await fetchContentfulData(
    {
      query: travelClinicQuery,
      variables: { preview }
    },
    preview
  )) as UnknownObjectAny;

  graphQLErrorHandler('travelClinicQuery', travelClinics?.errors, travelClinicQuery);
  graphQLErrorHandler('pageQuery', content?.errors, pageQuery);

  return {
    props: {
      content: content?.data?.pageCollection?.items?.[0],
      travelClinics: travelClinics?.data?.contentTypeLocationCollection?.items
    }
  };
}

export default TravelClinicLocations;
